import { useEffect, useRef } from 'react'
import { useMutation } from '@apollo/client'
import { CREATE_JITSI_LOG_MUTATION } from '@graphql'
import { JitsiMeeting } from '@jitsi/react-sdk'
import PropTypes from "prop-types"

const LOGGING_ENABLED = false

const JitsiVideoConference = ({ 
  apiRef, 
  bookingSlotId,
  event, 
  handleConferenceJoined, 
  handleConferenceLeft,
  handleJoinButtonClicked,
  handleParticipantJoined,
  handleParticipantLeft,
  jwtToken, 
  roomId 
}) => {
  const log = useRef([])

  // Just log errors - we don't want errors to block the user experience
  const [createJitsiLog] = useMutation(CREATE_JITSI_LOG_MUTATION, {
    onError: (error) => console.error("Error creating Jitsi log", error)
  })

  const handleUpdateLog = (type, eventId, roomId, bookingSlotId, payload) => {
    if (!LOGGING_ENABLED) return
    log.current.push({ type, event_id: eventId, room_id: roomId, booking_slot_id: bookingSlotId, payload })
  }

  const isPreJoinButtonClick = (data) => {
    return data.includes("Prejoin join button clicked.") && data.includes("features/prejoin")
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return () => {
      if (!LOGGING_ENABLED) return;

      if (log.current.length > 0) {
        createJitsiLog({ variables: { input: { log_entries: log.current } } })
      }
    }
  }, [])

  /**
   * Custom event listener to detect when the Jitsi pre-join button is clicked.
   * This is necessary because the Jitsi iframe does not provide a direct callback for this event.
   */
  useEffect(() => {
    const handlePostMessage = (event) => {
      if (event.origin !== `https://${process.env.REACT_APP_JITSI_URL}` || !event.data || typeof event.data !== 'string') {
        return
      }

      if (isPreJoinButtonClick(event.data)) {
        window.removeEventListener('message', handlePostMessage);
        handleJoinButtonClicked();
      }
    }

    window.addEventListener('message', handlePostMessage)

    return () => {
      window.removeEventListener('message', handlePostMessage)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <JitsiMeeting
      key={roomId}
      domain={process.env.REACT_APP_JITSI_URL}
      roomName={roomId}
      configOverwrite={{
        apiLogLevels: ['info'],
        disableDeepLinking: true, 
        subject: ' ',
        hideConferenceTimer: true,
        toolbarButtons: [
          'camera', 
          'chat', 
          'closedcaptions', 
          'desktop', 
          'download', 
          'etherpad',  
          'filmstrip', 
          'fodeviceselection', 
          'participants-pane',
          'fullscreen',
          'hangup', 
          'help', 
          'livestreaming', 
          'microphone', 
          'mute-everyone',
          'noise-surpression',
          'profile', 
          'raisehand', 
          // 'recording',
          'select-background',
          'settings', 
          'shortcuts',
          'stats', 
          'tileview', 
          'videoquality', 
        ]
      }}
      jwt={jwtToken}
      onApiReady={(externalApi) => {
        apiRef.current = externalApi

        if (LOGGING_ENABLED) {
          externalApi.on("cameraError", (payload) => {
            handleUpdateLog("CAMERA_ERROR", event.id, roomId, bookingSlotId, payload)
          })
        
          externalApi.on("micError", (payload) => {
            handleUpdateLog("MIC_ERROR", event.id, roomId, bookingSlotId, payload)
          })

          externalApi.on("deviceListChanged", (payload) => {
            handleUpdateLog("DEVICE_LIST_CHANGED", event.id, roomId, bookingSlotId, payload)
          })

          externalApi.on("audioAvailabilityChanged", (payload) => {
            handleUpdateLog("AUDIO_AVAILABILITY_CHANGED", event.id, roomId, bookingSlotId, payload)
          })
        
          externalApi.on("videoAvailabilityChanged", (payload) => {
            handleUpdateLog("VIDEO_AVAILABILITY_CHANGED", event.id, roomId, bookingSlotId, payload)
          })
        }

        externalApi.on('participantJoined', (payload) => {
          handleParticipantJoined(payload);
        });

        externalApi.on('participantLeft', (payload) => {
          handleParticipantLeft(payload);
        });

        externalApi.on("videoConferenceJoined", (payload) => {
          handleUpdateLog("VIDEO_CONFERENCE_JOINED", event.id, roomId, bookingSlotId, payload)
          handleConferenceJoined()
        })

        externalApi.on("videoConferenceLeft", (payload) => {
          handleUpdateLog("VIDEO_CONFERENCE_LEFT", event.id, roomId, bookingSlotId, payload)
          handleConferenceLeft()
        })
      }}
      // onReadyToClose={() => handleClose()}
      getIFrameRef={(iframeRef) => { 
        iframeRef.style.height = "100%" // is this correct?
        iframeRef.style.width = "100%" // is this correct?
      }}
    />
  )
}

JitsiVideoConference.propTypes = {
  bookingSlotId: PropTypes.string.isRequired,
  event: PropTypes.object.isRequired,
  handleConferenceJoined: PropTypes.func.isRequired,
  handleConferenceLeft: PropTypes.func.isRequired,
  handleJoinButtonClicked: PropTypes.func.isRequired,
  handleParticipantJoined: PropTypes.func.isRequired,
  handleParticipantLeft: PropTypes.func.isRequired,
  height: PropTypes.string,
  jwtToken: PropTypes.string.isRequired,
  roomId: PropTypes.string.isRequired,
  width: PropTypes.string,
}

export default JitsiVideoConference